import type { BriefTemplateLevel } from './types';

/** The per-level manage rights `usePermissions()` exposes; a `Permissions` object satisfies it. */
export interface BriefTemplateLevelAccess {
  canManageWorkspaceBriefTemplates: boolean;
  canManageOrganizationBriefTemplates: boolean;
  canManageSystemBriefTemplates: boolean;
}

export interface BriefTemplateActions {
  canEdit: boolean;
  canDelete: boolean;
  canDuplicate: boolean;
  canPromote: boolean;
  canToggle: boolean;
}

export function canManageBriefTemplateLevel(
  level: BriefTemplateLevel,
  access: BriefTemplateLevelAccess,
): boolean {
  switch (level) {
    case 'system':
      return access.canManageSystemBriefTemplates;
    case 'organization':
      return access.canManageOrganizationBriefTemplates;
    case 'workspace':
      return access.canManageWorkspaceBriefTemplates;
  }
}

/**
 * Mirrors the assets-api rules, so the UI never offers an action the API would refuse.
 *
 * An organization manager may switch a system default off and on for their own organization
 * without managing it. A duplicate is always created in the workspace, so it needs only the
 * workspace key; duplicating a system default is not offered yet.
 */
export function resolveBriefTemplateActions(
  level: BriefTemplateLevel,
  access: BriefTemplateLevelAccess,
): BriefTemplateActions {
  const canManage = canManageBriefTemplateLevel(level, access);

  return {
    canEdit: canManage,
    canDelete: canManage,
    canDuplicate: level !== 'system' && access.canManageWorkspaceBriefTemplates,
    canPromote: level === 'workspace' && access.canManageOrganizationBriefTemplates,
    canToggle: canManage || (level === 'system' && access.canManageOrganizationBriefTemplates),
  };
}
